import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, useTheme, Box, useMediaQuery, Tooltip } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CreateMainComponentTypeDialog } from 'src/screens/customer/mainComponents/MainComponentDashboard'
import { useParams } from 'react-router-dom'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import {
  VerticalTabsContainer,
  StyledTabs,
  StyledTab,
  IconCountRow,
  CountBox,
  TabTextBox,
  TabTextTypography,
  AddEquipmentButton,
} from './VerticalTabs.styles'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000))

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{ flex: 1, position: 'relative' }}
    >
      {value === index && (
        <>
          <Box sx={{ pt: isSmallScreen ? 2 : 0 }}>{children}</Box>
        </>
      )}
    </Box>
  )
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  }
}

interface VerticalTabsProps {
  tabs: {
    label: string
    content: React.ReactNode
    icon?: React.ReactNode
    count?: number
  }[]
  onAddClick?: () => void
  selectedIndex: number
  onTabChange: (newIndex: number) => void
  isDiscarded?: boolean
}

const VerticalTabs: React.FC<VerticalTabsProps> = ({ tabs, selectedIndex, onTabChange }) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000))
  const { id: customerId } = useParams()
  const { t: translate } = useTranslation(['main-component-type', 'common', 'main-component'])
  const { showDialog } = useDialogCtx()

  const handleChange = React.useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      onTabChange(newValue)
    },
    [onTabChange],
  )
  const onAddMainComponentType = React.useCallback(async () => {
    const added = await showDialog(
      CreateMainComponentTypeDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }, [showDialog, customerId])

  return (
    <Box sx={{ display: 'flex', height: window.innerHeight - 160 }}>
      <VerticalTabsContainer isSmallScreen={isSmallScreen}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{
            mb: 0,
            pl: '12px',
            pt: '18px',
            color: theme.palette.primary.main,
          }}
        >
          {translate('main-component-types', { ns: 'main-component-type' })}
        </Typography>
        <StyledTabs
          orientation="vertical"
          variant="scrollable"
          value={selectedIndex}
          onChange={handleChange}
          aria-label="Vertical tabs"
          TabIndicatorProps={{
            style: {
              width: '0px',
            },
          }}
          isSmallScreen={isSmallScreen}
        >
          {tabs.map((tab, index) => (
            <StyledTab
              key={index}
              {...a11yProps(index)}
              isSmallScreen={isSmallScreen}
              label={
                isSmallScreen ? (
                  <Tooltip title={tab.label} placement="right">
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <IconCountRow>
                        {tab.icon}
                        {tab.count !== undefined && (
                          <CountBox theme={theme}>
                            <Typography
                              color={theme.palette.primary.main}
                              variant="body2"
                              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
                            >
                              {String(tab.count).padStart(2, '0')}
                            </Typography>
                          </CountBox>
                        )}
                      </IconCountRow>
                      <TabTextBox>
                        <TabTextTypography variant="body1" fontWeight={500} theme={theme}>
                          <DynamicTextTypography
                            text={tab.label}
                            maxLength={12}
                            style={{
                              color:
                                selectedIndex === index ? theme.palette.background.paper : theme.palette.primary.dark,
                            }}
                          />
                        </TabTextTypography>
                      </TabTextBox>
                    </Box>
                  </Tooltip>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      pr: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.icon}
                      <Typography
                        color={selectedIndex === index ? theme.palette.background.paper : theme.palette.primary.dark}
                        variant="body1"
                        fontWeight={500}
                        sx={{ textTransform: 'none' }}
                      >
                        <DynamicTextTypography
                          text={tab.label}
                          maxLength={10}
                          style={{
                            color:
                              selectedIndex === index ? theme.palette.background.paper : theme.palette.primary.dark,
                          }}
                        />
                      </Typography>
                    </Box>
                    {tab.count !== undefined && (
                      <CountBox theme={theme}>
                        <Typography color={theme.palette.primary.main} variant="body2" sx={{ textTransform: 'none' }}>
                          {String(tab.count).padStart(2, '0')}
                        </Typography>
                      </CountBox>
                    )}
                  </Box>
                )
              }
            />
          ))}
        </StyledTabs>
        <Box sx={{ backgroundColor: 'white' }}>
          <AddEquipmentButton
            fullWidth
            variant="contained"
            size="small"
            onClick={onAddMainComponentType}
            isSmallScreen={isSmallScreen}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: 0,
            }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
            <Typography variant="body1" fontWeight={500}>
              {isSmallScreen && translate('add', { ns: 'common' })}
              {!isSmallScreen && translate('add-equipment-type', { ns: 'main-component-type' })}
            </Typography>
          </AddEquipmentButton>
        </Box>
      </VerticalTabsContainer>
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={selectedIndex} index={index}>
            <Box
              sx={
                isSmallScreen
                  ? {
                      height: 'calc(70vh - 24px)',
                      overflowY: 'auto',
                    }
                  : undefined
              }
              height={'90vh'}
            >
              {tab.content}
            </Box>
          </TabPanel>
        ))}
      </Box>
    </Box>
  )
}

export default VerticalTabs
